import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc
} from "firebase/firestore";
import { db } from "../firebase";
import Layout from "../components/Layout";
import { FaDownload, FaTruck, FaMapMarkerAlt, FaUserCheck, FaUserSlash } from "react-icons/fa";

function EWayBillGenerator() {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [skuMap, setSkuMap] = useState({});

  // Configuration Modal State
  const [showModal, setShowModal] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);
  const [distance, setDistance] = useState(50);
  const [retrySuffix, setRetrySuffix] = useState("");
  const [vehicleNo, setVehicleNo] = useState("");

  const openConfig = (o) => {
    setActiveOrder(o);
    setVehicleNo(o.vehicleNo || "");
    setShowModal(true);
  };

  useEffect(() => {
    fetchCompanyInfo();
    fetchSkuMaster();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchOrderData();
    }
  }, [selectedDate]);

  const fetchCompanyInfo = async () => {
    const snap = await getDoc(doc(db, "companyInfo", "default"));
    if (snap.exists()) setCompanyInfo(snap.data());
  };

  const fetchSkuMaster = async () => {
    const snap = await getDocs(collection(db, "skuMaster"));
    const sm = {};
    snap.forEach(d => {
        const data = d.data();
        sm[data.sku] = data;
    });
    setSkuMap(sm);
  };

  const fetchOrderData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Line data first for the date
      const qL = query(
        collection(db, "orderLines"),
        where("approvedDateOnly", "==", selectedDate),
        where("status", "==", "APPROVED")
      );
      const lineSnap = await getDocs(qL);
      const lines = lineSnap.docs.map(d => d.data());
      
      // 2. Extract unique order IDs
      const uniqueOrderIds = [...new Set(lines.map(l => l.orderId))];
      
      // 3. Fetch all matching Headers from outletOrders using rootOrderId field
      const headerMap = {};
      if (uniqueOrderIds.length > 0) {
        // Chunking by 10 for Firestore 'in' query limit
        for (let i = 0; i < uniqueOrderIds.length; i += 10) {
          const chunk = uniqueOrderIds.slice(i, i + 10);
          // Forensic Fix: Use "rootOrderId" field instead of "__name__" (Document ID)
          const qH = query(collection(db, "outletOrders"), where("rootOrderId", "in", chunk));
          const hSnap = await getDocs(qH);
          hSnap.forEach(d => { 
            const data = d.data();
            headerMap[data.rootOrderId] = data; 
          });
        }
      }

      // 4. Group and map docNo
      const orderGroups = {};
      lines.forEach(line => {
        const oid = line.orderId;
        const header = headerMap[oid] || {};
        const gstin = (header.gstno || header.gstin || line.gstno || "").trim();
        const isReg = gstin.length === 15;
        
        if (!orderGroups[oid]) {
          // Priority for GST Parties: outletOrders.displayOrderId (The Tax Invoice)
          // Priority for Non-GST Parties: orderLines.displayOrderId (The Challan)
          const finalDocNo = isReg ? (header.displayOrderId || oid) : (line.displayOrderId || oid);
          
          orderGroups[oid] = {
            id: oid,
            displayOrderId: line.displayOrderId || oid,
            docNo: finalDocNo,
            partyId: line.partyId,
            partyName: header.outletName || line.partyName || "Unknown",
            gstin: gstin,
            address: header.address || "",
            city: header.city || "",
            pincode: header.pincode || "",
            stateCode: header.stateCode || "29",
            vehicleNo: header.vehicleNo || line.vehicleNo || "N/A",
            date: line.approvedDateOnly,
            isRegistered: isReg,
            lines: []
          };
        }
        orderGroups[oid].lines.push(line);
      });

      setOrders(Object.values(orderGroups));
    } catch (err) {
      console.error("Error fetching unified eway data:", err);
    }
    setLoading(false);
  };

  const round2 = (num) => Math.round((Number(num) + Number.EPSILON) * 100) / 100;

  const cleanDocNo = (no) => {
    if (!no) return "";
    let cleaned = no.replace(/[^a-zA-Z0-9\-\/]/g, "").replace(/_/g, "");
    while (cleaned.length > 0 && (cleaned.startsWith("0") || cleaned.startsWith("-") || cleaned.startsWith("/"))) {
      cleaned = cleaned.substring(1);
    }
    return cleaned;
  };

  const handleDownloadInit = (order) => {
    setActiveOrder(order);
    setVehicleNo(order.vehicleNo || "");
    setShowModal(true);
  };

  const generateJson = () => {
    if (!activeOrder || !companyInfo) return;

    try {
      const isRegistered = activeOrder.isRegistered;
      const [year, month, day] = activeOrder.date.split("-");
      const formattedDate = `${day}/${month}/${year}`;
      
      const finalDocNo = cleanDocNo(activeOrder.docNo) + (retrySuffix ? `-${retrySuffix}` : "");

      // Process Items with strict rounding
      const processedItems = activeOrder.lines.map((l, idx) => {
        const sku = skuMap[l.sku] || {};
        return {
          itemNo: idx + 1,
          productName: l.skuName || l.description || "Product",
          productDesc: l.skuName || l.description || "Product",
          hsnCode: Number(l.hsn || 0),
          quantity: round2(l.qty || 0),
          qtyUnit: sku.unitType || "THD",
          taxableAmount: round2(l.basicAmount),
          sgstRate: round2(l.gstPercent / 2 || 0),
          cgstRate: round2(l.gstPercent / 2 || 0),
          igstRate: 0,
          cessRate: 0,
          cessNonAdvol: 0
        };
      });

      const headerTaxable = round2(processedItems.reduce((acc, item) => acc + item.taxableAmount, 0));
      
      // Use gstAmount directly from line items as per user requirement
      const headerCgst = round2(activeOrder.lines.reduce((acc, l) => acc + round2((l.gstAmount || 0) / 2), 0));
      const headerSgst = round2(activeOrder.lines.reduce((acc, l) => acc + round2((l.gstAmount || 0) / 2), 0));
      
      const headerTotalValue = round2(headerTaxable + headerCgst + headerSgst);

      const payload = {
        version: "1.0.1020",
        billLists: [
          {
            genMode: "Excel",
            userGstin: companyInfo.gstin || "",
            supplyType: "O",
            subSupplyType: isRegistered ? 1 : 10, // 1 for B2B Supply, 10 for Line Sales (Delivery Challan)
            docType: isRegistered ? "INV" : "CHL",
            transType: 1,
            docNo: finalDocNo,
            docDate: formattedDate,
            fromGstin: companyInfo.gstin || "",
            fromTrdName: companyInfo.name || "M D SONS",
            fromAddr1: companyInfo.address1 || "",
            fromPlace: companyInfo.place || "",
            fromPincode: Number(companyInfo.pincode || 560002),
            fromStateCode: Number(companyInfo.statecode || 29),
            actualFromStateCode: Number(companyInfo.statecode || 29),
            toGstin: isRegistered ? activeOrder.gstin : companyInfo.gstin,
            toTrdName: isRegistered ? activeOrder.partyName : "",
            toAddr1: isRegistered ? activeOrder.address : "",
            toPlace: isRegistered ? activeOrder.city : "",
            toPincode: isRegistered ? Number(activeOrder.pincode || 0) : 560000,
            toStateCode: isRegistered ? Number(activeOrder.stateCode || 29) : Number(companyInfo.statecode || 29),
            actualToStateCode: isRegistered ? Number(activeOrder.stateCode || 29) : Number(companyInfo.statecode || 29),
            totalValue: headerTaxable,
            cgstValue: headerCgst,
            sgstValue: headerSgst,
            igstValue: 0,
            cessValue: 0,
            totInvValue: headerTotalValue,
            transMode: 1,
            transDistance: Number(distance),
            transporterName: "",
            transporterId: "",
            vehicleNo: vehicleNo,
            vehicleType: "R",
            mainHsnCode: Number([...activeOrder.lines].sort((a, b) => (Number(b.basicAmount) || 0) - (Number(a.basicAmount) || 0))[0]?.hsn || 0),
            itemList: processedItems,
            totNonAdvolVal: 0
          }
        ]
      };

      console.log(`Generated ${isRegistered ? 'B2B' : 'Challan'} Payload:`, payload);

      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      
      // Standardized Naming: EWB-PARTYID-DOCNO.json
      const cleanPartyId = (activeOrder.partyId || "N-A").substring(0, 10).replace(/[^a-zA-Z0-9]/g, "");
      const fileName = `EWB-${cleanPartyId}-${finalDocNo}.json`;
      
      a.download = fileName;
      a.click();
      setShowModal(false);
    } catch (err) {
      console.error("JSON Generation Error:", err);
      alert("Error: " + err.message);
    }
  };

  return (
    <Layout>
      <div style={{ padding: "30px", maxWidth: "1300px", margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
          <div>
            <h1 style={{ fontSize: "28px", fontWeight: "800", color: "#1e293b", margin: 0 }}>E-Way Bill Command Center</h1>
            <p style={{ color: "#64748b", marginTop: "5px" }}>Unified Generator for B2B Supply & Delivery Challans</p>
          </div>
          <div style={{ background: "white", padding: "12px 24px", borderRadius: "12px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0" }}>
            <label style={{ fontSize: "11px", fontWeight: "800", color: "#94a3b8", display: "block", textTransform: "uppercase" }}>Audit Date</label>
            <input 
              type="date" 
              value={selectedDate} 
              onChange={(e) => setSelectedDate(e.target.value)}
              style={{ border: "none", outline: "none", fontSize: "16px", fontWeight: "700", color: "#1e293b" }}
            />
          </div>
        </div>

        <div style={{ background: "white", borderRadius: "16px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)", overflow: "hidden", border: "1px solid #f1f5f9" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Doc Number</th>
                <th style={thStyle}>Party / Destination</th>
                <th style={thStyle}>Logistics</th>
                <th style={thStyle}>Value</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => {
                const isReg = o.gstin && o.gstin.length === 15;
                const totalVal = o.lines.reduce((acc, l) => acc + round2(l.totalAmount || l.lineTotal || 0), 0);
                
                return (
                  <tr key={o.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                    <td style={tdStyle}>
                      {isReg ? (
                        <span style={{ background: "#ecfdf5", color: "#059669", padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "800", display: "flex", alignItems: "center", gap: "5px", width: "fit-content" }}>
                          <FaUserCheck size={12} /> B2B SUPPLY
                        </span>
                      ) : (
                        <span style={{ background: "#fef2f2", color: "#dc2626", padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "800", display: "flex", alignItems: "center", gap: "5px", width: "fit-content" }}>
                          <FaUserSlash size={12} /> CHALLAN
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: "700", color: "#1e293b" }}>{o.docNo}</div>
                      <div style={{ fontSize: "11px", color: "#94a3b8" }}>{o.isRegistered ? 'INV' : 'CHL'} REF: {o.displayOrderId}</div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: "600", color: "#334155" }}>{o.partyName}</div>
                      <div style={{ fontSize: "11px", color: "#64748b" }}>{o.gstin || "Unregistered Person (URP)"}</div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontSize: "13px", color: "#475569", fontWeight: "600", display: "flex", alignItems: "center", gap: "6px" }}>
                        <FaTruck size={14} color="#94a3b8" /> {o.vehicleNo}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, fontWeight: "800", color: "#1e293b" }}>₹{totalVal.toFixed(2)}</td>
                    <td style={tdStyle}>
                      <button 
                        onClick={() => handleDownloadInit(o)}
                        style={{ padding: "8px 16px", background: "#4f46e5", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "700", display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", transition: "all 0.2s" }}
                      >
                        <FaDownload size={14} /> JSON
                      </button>
                    </td>
                  </tr>
                );
              })}
              {orders.length === 0 && !loading && (
                <tr>
                  <td colSpan="6" style={{ padding: "60px", textAlign: "center", color: "#94a3b8" }}>
                    <div style={{ fontSize: "40px", marginBottom: "10px" }}>🚚</div>
                    No orders found for the selected date.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(15, 23, 42, 0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(4px)" }}>
            <div style={{ background: "white", padding: "32px", borderRadius: "24px", width: "450px", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}>
              <h2 style={{ margin: "0 0 8px 0", fontSize: "22px", fontWeight: "800", color: "#1e293b" }}>Configuration</h2>
              <p style={{ margin: "0 0 24px 0", color: "#64748b", fontSize: "14px" }}>Finalizing transport data for {activeOrder?.displayOrderId}</p>
              
              <div style={{ marginBottom: "20px" }}>
                <label style={labelStyle}>RETRY SUFFIX</label>
                <input 
                  type="text" 
                  placeholder="Leave blank unless duplicate error"
                  value={retrySuffix}
                  onChange={(e) => setRetrySuffix(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={labelStyle}>VEHICLE NUMBER</label>
                <input 
                  type="text" 
                  placeholder="e.g. KA 00 00 0000"
                  value={vehicleNo}
                  onChange={(e) => setVehicleNo(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: "30px" }}>
                <label style={labelStyle}>TRANSIT DISTANCE (KM)</label>
                <div style={{ position: "relative" }}>
                  <FaMapMarkerAlt style={{ position: "absolute", left: "14px", top: "14px", color: "#94a3b8" }} />
                  <input 
                    type="number" 
                    value={distance}
                    onChange={(e) => setDistance(e.target.value)}
                    style={{ ...inputStyle, paddingLeft: "40px" }}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "12px" }}>
                <button 
                  onClick={() => setShowModal(false)}
                  style={{ flex: 1, padding: "14px", borderRadius: "12px", border: "1px solid #e2e8f0", background: "white", fontWeight: "700", cursor: "pointer", color: "#475569" }}
                >
                  Cancel
                </button>
                <button 
                  onClick={generateJson}
                  style={{ flex: 2, padding: "14px", borderRadius: "12px", border: "none", background: "#4f46e5", color: "white", fontWeight: "700", cursor: "pointer", boxShadow: "0 4px 6px -1px rgba(79, 70, 229, 0.4)" }}
                >
                  Generate & Download
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

const thStyle = { padding: "16px 20px", fontSize: "12px", fontWeight: "800", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" };
const tdStyle = { padding: "16px 20px", fontSize: "14px" };
const labelStyle = { display: "block", fontSize: "12px", fontWeight: "800", color: "#475569", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.025em" };
const inputStyle = { width: "100%", padding: "12px 16px", borderRadius: "12px", border: "1px solid #e2e8f0", outline: "none", fontSize: "16px", fontWeight: "600", transition: "border-color 0.2s" };

export default EWayBillGenerator;
